import React, { useState, useEffect } from "react";
import styled from "styled-components";
import RenderColumns from "./RenderColumns";
import useBreakpoint from "hooks/useBreakpoint";
import { SkeletonTable } from "components/Table/SkeletonTable/SkeletonTable";
import { Table as RsuiteTable } from "rsuite";
import { renderColumns } from "./SimpleRenderColumns";

import type { TableProps as RsuiteTableProps } from "rsuite";
import type { TableColumn } from "./RenderColumns";
import type { Breakpoint } from "hooks/useBreakpoint";
import type { RowDataType } from "rsuite/esm/Table";


const { Column, HeaderCell, Cell } = RsuiteTable;


const StyledRsuiteTable = styled(RsuiteTable)`
    && {
        width: 100% !important;
        min-width: 480px;

        background: var(--color-bg);
        color: var(--color-text);
        border-radius: 0.75rem;
        overflow: hidden;
        border: 1px solid var(--color-border);

        .rs-table-cell {
            background: var(--color-bg);
            color: var(--color-text);
            border-color: var(--color-border);
            border-bottom: 1px solid var(--color-border-light);
        }
        .rs-table-row.current_row .rs-table-cell {
            background: var(--color-hover-bg);
        }

        .rs-table-row:not(.rs-table-row-header) .rs-table-cell .rs-table-cell-content {
            padding: ${({ rowHeight = 46 }) => rowHeight ? (0.3 * Number(rowHeight) - 2) : 13}px 10px;
        }
        .rs-table-row-header {
            background: var(--color-bg-dark);
            color: var(--color-text-dark);
            font-family: "Lato";
            font-size: 14px;
            font-weight: 600;
            border-color: var(--color-border);
        }
        .rs-table-row-header .rs-table-cell-content {
            padding: 8px 10px;
        }
        .rs-table-row {
            background: var(--color-bg);
            transition: background 0.15s;
            border-bottom: none !important;
        }
        && .rs-table-row:hover {
            background-color: var(--color-hover-bg) !important;
        }
        .rs-table-hover .rs-table-row:hover .rs-table-cell {
            background-color: var(--color-hover-bg) !important;
        }
        &&.rs-table-hover .rs-table-row:hover .rs-table-cell {
            background-color: var(--color-hover-bg) !important;
        }
        .rs-table-cell-content {
            color: var(--color-text);
        }

        &&.virtualized .rs-table-body-wheel-area {
            width: 0% !important;
        }
    }
`;

const TableTitle = styled.div`
    && {
        margin-top: 16px;
        margin-bottom: 8px;
        font-size: 24px;
        font-weight: 600;
        color: var(--color-text)
    }  
`;

interface DarkTableProps extends RsuiteTableProps {
    columns?: TableColumn[];
    children?: React.ReactNode;
    title?: string;
    loading?: boolean;
    error?: boolean;
    skeletonRows?: number;
    headerHeight?: number;
    rowHeight?: number;
    showSkeleton?: boolean;
    showError?: boolean;
    rowClassName?: ((rowData: any, rowIndex: number) => string) | undefined;
};

const DarkTable = React.forwardRef<any, DarkTableProps>((props, ref) => {
    const {
        columns,
        children,

        rowHeight: rowHeightProp,
        headerHeight: headerHeightProp,

        title,
        loading = false,
        error,
        rowKey,
        className = "",
        skeletonRows = 5,
        showSkeleton = false,
        showError = false,
        virtualized = false,

        ...restProps
    } = props;

    // const [showLoader, setShowLoader] = useState(loading);

    // useEffect(() => {
    //     if (loading) {
    //         setShowLoader(true);
    //     } else {
    //         // після завершення запиту ще 300 мс тримаємо loader
    //         const t = setTimeout(() => setShowLoader(false), 300);
    //         return () => clearTimeout(t);
    //     }
    // }, [loading]);

    const breakpoint: Breakpoint = useBreakpoint();

    const renderedColumns = columns
        ? columns.map((column) => RenderColumns(column, breakpoint))
        : [];

    const defaultRowHeight = 46;
    const defaultHeaderHeight = 46;

    const headerHeight = headerHeightProp ?? defaultHeaderHeight;
    const rowHeight = rowHeightProp ?? defaultRowHeight;

    return <>
        {title && (
            <TableTitle>
                {title}
            </TableTitle>
        )}
        {loading && showSkeleton
            ? (<SkeletonTable
                rows={skeletonRows}
                columns={columns}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
            />)
            : error && showError
                ? (<div>Error loading data</div>)
                : (<StyledRsuiteTable
                    ref={ref}
                    rowKey={rowKey}
                    rowHeight={rowHeight}
                    headerHeight={headerHeight}
                    className={(virtualized ? "virtualized" : "") + (className ? ` ${className}` : "")}
                    // loading={loading}
                    virtualized={virtualized}
                    {...restProps}
                >
                    {columns ? renderedColumns : children}
                </StyledRsuiteTable>)}
    </>;
});

export { Column, HeaderCell, Cell };
export default DarkTable; 